import React, { useEffect, useRef, useState } from "react";
import { T } from "../theme/tokens.js";
import { Chip } from "./ui/index.jsx";

/* Editing a value where it sits.
 *
 * Two controls, used by the registers and by trackers alike, because a status
 * meeting does not care which table it is looking at. Opening a form to change
 * one dropdown costs six clicks and your place in the list, so a dropdown
 * column opens a short menu under the chip and everything else turns into an
 * input where it is.
 *
 * Both save the moment you are done rather than on a separate Save, and both
 * show the value greyed while the write is in flight, so a slow connection
 * looks like a slow save rather than like nothing happening.
 *
 * The menu is positioned fixed, measured from the chip's own rectangle. These
 * tables scroll sideways inside a box that would otherwise clip it.
 */
export const inputStyle = {
  width: "100%", padding: "8px 10px", border: `1px solid ${T.line}`,
  borderRadius: 4, fontSize: 14, background: "#fff", color: T.ink,
};

export const menuItem = (selected) => ({
  display: "block", width: "100%", textAlign: "left", cursor: "pointer",
  background: selected ? T.chip : "transparent", border: "none",
  borderRadius: 3, padding: "6px 8px", font: "inherit",
});

export const quietButton = {
  background: "none", border: "none", borderBottom: `1px dotted ${T.soft}77`,
  padding: 0, margin: 0, font: "inherit", color: "inherit", textAlign: "left",
  cursor: "pointer", maxWidth: "100%",
};

export function QuickPick({ current, value, options, renderOption, onPick }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);

  const show = () => {
    const r = ref.current.getBoundingClientRect();
    const room = window.innerHeight - r.bottom;
    const tall = Math.min(options.length + 1, 9) * 32 + 16;
    setPos(room > tall
      ? { top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 220) }
      : { bottom: window.innerHeight - r.top + 4, left: Math.min(r.left, window.innerWidth - 220) });
    setOpen(true);
  };

  const choose = async (next) => {
    setOpen(false);
    if (next === (value ?? null)) return;
    setBusy(true);
    await onPick(next);
    setBusy(false);
  };

  return (
    <>
      <button ref={ref} type="button" onClick={show} title="Click to change"
        style={{ ...quietButton, opacity: busy ? 0.4 : 1,
          /* A chip already looks like something you can press. A bare word in a
             dropdown column does not, so that one gets the underline. */
          borderBottom: React.isValidElement(current) && current.type === Chip
            ? "none" : quietButton.borderBottom }}>
        {current}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 60 }} />
          <div style={{
            position: "fixed", ...pos, zIndex: 61, minWidth: 190, maxHeight: 300,
            overflowY: "auto", background: "#fff", border: `1px solid ${T.line}`,
            borderRadius: 4, boxShadow: "0 8px 22px rgba(20,40,75,0.18)", padding: 4,
          }}>
            {options.map((o) => (
              <button key={o} type="button" style={menuItem(o === value)}
                onClick={() => choose(o)}>
                {renderOption(o)}
              </button>
            ))}
            <button type="button" style={{ ...menuItem(false), borderTop: `1px solid ${T.line}`,
              marginTop: 4, paddingTop: 8, borderRadius: 0 }}
              onClick={() => choose(null)}>
              <span style={{ color: T.soft, fontSize: 12 }}>Clear</span>
            </button>
          </div>
        </>
      )}
    </>
  );
}

export function InlineEdit({ value, type, display, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);

  /* Only while nobody is in the box. Somebody else's change arriving live
     must not overwrite what this person is halfway through typing. */
  useEffect(() => { if (!editing) setDraft(value ?? ""); }, [value, editing]);

  const commit = async () => {
    setEditing(false);
    const next = draft === "" ? null : draft;
    if (next === (value ?? null)) return;
    setBusy(true);
    await onSave(next);
    setBusy(false);
  };

  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} title="Click to change"
        style={{ ...quietButton, opacity: busy ? 0.4 : 1 }}>{display}</button>
    );
  }
  return (
    <input autoFocus type={type} value={draft} onBlur={commit}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
        if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); }
      }}
      style={{ ...inputStyle, padding: "3px 6px", fontSize: 13,
               minWidth: type === "date" ? 138 : 110 }} />
  );
}
